import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Server,
  Book,
  Database,
  Tag,
  Layers,
  Box,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  FlaskConical,
  RefreshCw,
  Upload,
} from 'lucide-react'
import dashboardService, {
  DashboardMyActivitiesResponse,
  DashboardSummaryResponse,
  MonthlyCount,
} from '../services/dashboardService'
import systemService, { SystemStatusResponse } from '../services/systemService'
import { hasPermission } from '../utils/permissions'
import { PERMISSIONS } from '../constants/permissions'
import { formatDatetimeWithSeconds, formatLocaleDatetime, formatLocaleDateOnly } from '../utils/date'

interface DashboardProps {
  userInfo: {
    email: string
    fullName: string
    isAdmin: boolean
    permissions: string[]
  }
}

const formatBytesHuman = (bytes: number): string => {
  const gb = bytes / (1024 * 1024 * 1024)
  if (!Number.isFinite(gb)) return '—'
  if (gb >= 1024) return `${(gb / 1024).toFixed(1)}TB`
  // 1GB 미만은 MB 로 — 막 시작한 버킷이 0.0GB 로 보이지 않게
  if (gb < 1) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
  return `${gb.toFixed(1)}GB`
}


// 1. 시스템 상태 바 (호스트 기준 + UPLOAD_DIR 마운트 기준 디스크)
const SystemStatusStrip: React.FC<{ status: SystemStatusResponse | null }> = ({ status }) => {
  const cpu = status?.cpu_percent
  const cpuText = cpu == null ? '—' : `${Math.round(cpu)}%`

  const memUsed = status?.memory.used_bytes
  const memTotal = status?.memory.total_bytes
  const ramPct =
    memUsed != null && memTotal != null && memTotal > 0 ? (memUsed / memTotal) * 100 : null
  const ramDetail =
    memUsed != null && memTotal != null ? `${formatBytesHuman(memUsed)}/${formatBytesHuman(memTotal)}` : null
  const ramText =
    ramPct != null && ramDetail != null ? `${ramPct.toFixed(1)}%(${ramDetail})` : '—'

  const diskUsed = status?.disk?.used_bytes
  const diskTotal = status?.disk?.total_bytes
  const diskPct =
    diskUsed != null && diskTotal != null && diskTotal > 0
      ? (diskUsed / diskTotal) * 100
      : null
  const diskWarn = diskPct != null && diskPct >= 85
  const diskDetail =
    diskUsed != null && diskTotal != null ? `${formatBytesHuman(diskUsed)}/${formatBytesHuman(diskTotal)}` : null
  // 오브젝트 스토리지(S3) 배치는 마운트가 없어 disk 가 비고, 데이터 버킷 합계가 온다 — 상한이 없다
  const storage = status?.storage
  const diskText =
    diskPct != null && diskDetail != null
      ? `${diskPct.toFixed(1)}%(${diskDetail})${diskWarn ? '(주의)' : ''}`
      : storage
        ? `${formatBytesHuman(storage.used_bytes)} / 제한없음`
        : '—'
  const diskPath = status?.disk?.path
  const diskTitle = diskPath
    ? `${diskPath} • ${diskText}`
    : storage
      ? `오브젝트 스토리지(S3) 데이터 버킷 합계 • 객체 ${storage.object_count.toLocaleString()}개 • 집계 ${formatDatetimeWithSeconds(storage.measured_at)}`
      : diskText

  const gpuAvailable = status?.gpu.available
  const firstGpu = status?.gpu.gpus?.[0]
  const gpuText = !gpuAvailable
    ? '미탐지'
    : `${firstGpu?.util_percent != null ? Math.round(firstGpu.util_percent) : '—'}% (${
        firstGpu?.temp_c != null ? Math.round(firstGpu.temp_c) : '—'
      }°C)`

  return (
    <div className="mb-6 flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-700">
      <div className="flex items-center gap-6">
        <span className="flex items-center gap-2 font-bold text-slate-800">
          <Server size={18} className="text-slate-500" /> 시스템 상태
        </span>
        <div className="h-4 w-px bg-slate-300" />
        <span className="flex items-center gap-2">
          CPU{' '}
          <span className="rounded bg-blue-50 px-2 py-0.5 font-bold text-blue-600">{cpuText}</span>
        </span>
        <span className="flex items-center gap-2">
          RAM{' '}
          <span className="rounded bg-purple-50 px-2 py-0.5 font-bold text-purple-600">{ramText}</span>
        </span>
        <span className="flex items-center gap-2">
          GPU{' '}
          <span className="rounded bg-green-50 px-2 py-0.5 font-bold text-green-600">{gpuText}</span>
        </span>
        <span className="flex items-center gap-2">
          Disk{' '}
          <span
            className="rounded bg-orange-50 px-2 py-0.5 font-bold text-orange-600"
            title={diskTitle || undefined}
          >
            {diskText}
          </span>
        </span>
      </div>
      <div className="flex items-center gap-1 text-xs text-slate-400">
        <Clock size={12} /> 업데이트: {formatDatetimeWithSeconds(status?.updated_at)}
      </div>
    </div>
  )
}

// 2. 파이프라인 카드 (수정됨: 한글 타이틀)
interface PipelineCardProps {
  title: string
  count: string
  sub: string
  icon: React.ComponentType<{ size?: number }>
  color: string
  badgeText?: string
  badgeColor?: string
}

const PipelineCard: React.FC<PipelineCardProps> = ({
  title,
  count,
  sub,
  icon: Icon,
  color,
  badgeText,
  badgeColor,
}) => (
  <div className="group relative flex h-32 flex-col justify-between overflow-hidden rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:border-slate-300">
    <div className={`absolute right-0 top-0 p-3 opacity-10 transition-opacity group-hover:opacity-20 ${color}`}>
      <Icon size={60} />
    </div>
    <div>
      <h3 className="mb-1 flex items-center gap-2 text-sm font-bold tracking-wide text-slate-500">
        <Icon size={16} /> {title}
      </h3>
      <div className="mt-2 text-3xl font-extrabold text-slate-800">{count}</div>
    </div>
    <div className="mt-2 flex items-end justify-between">
      <div className="text-xs font-medium text-slate-400">{sub}</div>
      {badgeText && badgeColor && (
        <div className={`rounded-full px-2 py-1 text-xs font-bold ${badgeColor}`}>{badgeText}</div>
      )}
    </div>
  </div>
)

type MonthlyChartDatum = { month: string; total: number; auto: number; manual: number }

// 간단한 월별 Grouped Bar 차트 (차트 라이브러리 없이 구현)
interface MonthlyBarChartProps {
  data: MonthlyChartDatum[]
}

const MonthlyBarChart: React.FC<MonthlyBarChartProps> = ({ data }) => {
  const [hovered, setHovered] = useState<{
    month: string
    total: number
    auto: number
    manual: number
  } | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null)
  const plotRef = useRef<HTMLDivElement | null>(null)

  const maxTotal = Math.max(...data.map((d) => d.total), 1)
  // Y축 눈금: 4등분, 50 단위로 올림
  const approxStep = Math.max(50, Math.ceil(maxTotal / 4 / 50) * 50)
  const maxTick = approxStep * 4
  const ticks = [0, approxStep, approxStep * 2, approxStep * 3, approxStep * 4]

  const maxBarHeight = 180

  return (
    <div className="flex h-72 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 pb-6 pt-3">
      {/* Y축 레이블 */}
      <div className="flex w-12 flex-col justify-between py-1 pr-2 text-[11px] text-slate-400">
        {ticks
          .slice()
          .reverse()
          .map((t) => (
            <div key={t} className="flex flex-1 items-center justify-end">
              {t.toLocaleString()}
            </div>
          ))}
      </div>

      {/* 그리드 + 막대 영역 */}
      <div ref={plotRef} className="relative flex-1 py-1">
        {/* 수평 그리드 라인 */}
        <div className="absolute inset-0 flex flex-col justify-between">
          {ticks
            .slice()
            .reverse()
            .map((t) => (
              <div
                key={t}
                className={
                  t === 0
                    ? 'border-t border-slate-300'
                    : 'border-t border-dashed border-slate-200'
                }
              />
            ))}
        </div>

        {/* 실제 막대들 (자동/수동 나란히 Grouped Bar) */}
        <div className="relative flex h-full items-end gap-4 px-2 pb-2">
          {data.map((d) => {
            const auto = d.auto
            const manual = d.manual

            const autoHeight = (auto / maxTick) * maxBarHeight
            const manualHeight = (manual / maxTick) * maxBarHeight

            // 0건이어도 위치가 보이도록 아주 얇은 막대를 표시
            const zeroBarHeight = 2
            const safeAutoHeight = auto > 0 ? Math.max(4, autoHeight) : zeroBarHeight
            const safeManualHeight = manual > 0 ? Math.max(4, manualHeight) : zeroBarHeight

            const tooltipText = `${d.month} - 총 ${d.total.toLocaleString()}건\n자동 ${auto.toLocaleString()}건 / 수동 ${manual.toLocaleString()}건`

            return (
              <div
                key={d.month}
                className="flex flex-1 cursor-pointer flex-col items-center justify-end text-[11px]"
                onMouseEnter={() =>
                  setHovered({
                    month: d.month,
                    total: d.total,
                    auto,
                    manual,
                  })
                }
                onMouseMove={(e) => {
                  const rect = plotRef.current?.getBoundingClientRect()
                  if (!rect) return
                  const x = e.clientX - rect.left + 12
                  const y = e.clientY - rect.top + 12
                  setTooltipPos({ x, y })
                }}
                onMouseLeave={() => {
                  setHovered(null)
                  setTooltipPos(null)
                }}
              >
                {/* Grouped Bar: 자동(파랑) / 수동(회색) 나란히 */}
                <div className="flex h-[180px] items-end justify-center gap-1">
                  {/* 자동 수집 */}
                  <div
                    className={`w-3 rounded-t-[2px] ${auto > 0 ? 'bg-blue-500' : 'bg-blue-200 opacity-60'}`}
                    style={{ height: safeAutoHeight }}
                  />
                  {/* 수동 수집 */}
                  <div
                    className={`relative w-3 rounded-t-[2px] ${
                      manual > 0 ? 'bg-slate-300' : 'bg-slate-200 opacity-70'
                    }`}
                    style={{ height: safeManualHeight }}
                  />
                </div>

                {/* X축 라벨: 월 */}
                <div className="mt-2 text-[11px] text-slate-500">{d.month}</div>

                {/* 접근성/백업용 텍스트(네이티브 title 대신) */}
                <span className="sr-only">{tooltipText}</span>
              </div>
            )
          })}
        </div>

        {/* Tooltip */}
        {hovered && tooltipPos && (
          <div
            className="pointer-events-none absolute z-10 min-w-[140px] whitespace-nowrap rounded-md bg-slate-900 px-[15px] py-[10px] text-[11px] leading-tight text-white shadow"
            style={{ left: tooltipPos.x, top: tooltipPos.y }}
          >
            <div className="font-semibold">{hovered.month}</div>
            <div>총 {hovered.total.toLocaleString()}건</div>
            <div className="mt-1 space-y-1">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-sm bg-blue-500" />
                <span>자동 수집: {hovered.auto.toLocaleString()}건</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-sm bg-slate-300" />
                <span>수동 수집: {hovered.manual.toLocaleString()}건</span>
              </div>
            </div>
          </div>
        )}

        {/* 범례: 우측 상단 배치 */}
        <div className="pointer-events-none absolute right-2 top-2 flex gap-4 text-[11px] text-slate-500">
          <div className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 bg-blue-500" />
            자동 수집
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 bg-slate-300" />
            수동 수집
          </div>
        </div>
      </div>
    </div>
  )
}

// 3. 메인 대시보드 레이아웃
const Dashboard: React.FC<DashboardProps> = ({ userInfo }) => {
  const [summary, setSummary] = useState<DashboardSummaryResponse | null>(null)
  const [activities, setActivities] = useState<DashboardMyActivitiesResponse | null>(null)
  const [systemStatus, setSystemStatus] = useState<SystemStatusResponse | null>(null)
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasLoadedOnceRef = useRef(false)

  const allMonths: string[] = [
    '1월',
    '2월',
    '3월',
    '4월',
    '5월',
    '6월',
    '7월',
    '8월',
    '9월',
    '10월',
    '11월',
    '12월',
  ]

  useEffect(() => {
    let cancelled = false

    const fetchLiveData = async (opts?: { showLoading?: boolean }) => {
      try {
        const showLoading = opts?.showLoading ?? false
        if (showLoading) setLoading(true)
        setError(null)

        const results = await Promise.allSettled([
          dashboardService.getSummary(selectedYear ?? undefined),
          dashboardService.getMyActivities(10),
          systemService.getStatus(),
        ])

        const summaryResult = results[0]
        const activitiesResult = results[1]
        const systemResult = results[2]

        if (summaryResult.status === 'fulfilled') {
          setSummary(summaryResult.value)
        } else {
          throw summaryResult.reason
        }

        if (activitiesResult.status === 'fulfilled') {
          setActivities(activitiesResult.value)
        }

        if (systemResult.status === 'fulfilled') {
          setSystemStatus(systemResult.value)
        }

        if (cancelled) return
        hasLoadedOnceRef.current = true

        // 최초 로딩 시 기본 연도 설정(가능하면 API가 알려주는 연도 사용)
        if (selectedYear == null) {
          const s = summaryResult.status === 'fulfilled' ? summaryResult.value : null
          const defaultYear =
            s?.raw.selected_year ??
            (s?.raw.available_years && s.raw.available_years.length > 0
              ? s.raw.available_years[s.raw.available_years.length - 1]
              : null)
          if (defaultYear != null) {
            setSelectedYear(defaultYear)
          }
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || '대시보드 데이터를 불러오지 못했습니다.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    // 첫 진입 시에는 로딩 표시
    fetchLiveData({ showLoading: !hasLoadedOnceRef.current })

    // 실시간 동기화: 주기적 폴링
    const intervalId = window.setInterval(() => {
      fetchLiveData({ showLoading: false })
    }, 15000)

    // 포커스/탭 복귀 시 즉시 재동기화
    const onFocus = () => fetchLiveData({ showLoading: false })
    window.addEventListener('focus', onFocus)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
      window.removeEventListener('focus', onFocus)
    }
    // selectedYear 변경 시 해당 연도 기준으로 즉시 재조회 + 이후 폴링 유지
  }, [selectedYear])

  const chartData: MonthlyChartDatum[] = useMemo(() => {
    const empty: MonthlyChartDatum[] = allMonths.map((m) => ({
      month: m,
      total: 0,
      auto: 0,
      manual: 0,
    }))

    const monthly: MonthlyCount[] | undefined = summary?.raw_monthly_uploads
    if (!monthly || monthly.length === 0) {
      return empty
    }

    const byMonth = new Map<number, MonthlyCount>()
    monthly.forEach((row) => {
      const mm = parseInt(row.period.slice(5), 10)
      if (!Number.isNaN(mm)) {
        byMonth.set(mm, row)
      }
    })

    return allMonths.map((label, idx) => {
      const m = idx + 1
      const row = byMonth.get(m)
      const auto = row?.auto_count ?? 0
      const manual = row?.manual_count ?? 0
      const total = auto + manual
      return { month: label, total, auto, manual }
    })
  }, [allMonths, summary])

  const chartTotal = useMemo(() => chartData.reduce((sum, d) => sum + d.total, 0), [chartData])
  const chartAuto = useMemo(() => chartData.reduce((sum, d) => sum + d.auto, 0), [chartData])
  const chartManual = useMemo(() => chartData.reduce((sum, d) => sum + d.manual, 0), [chartData])

  const formatCount = (n: number | null | undefined): string => {
    if (n == null) return '—'
    return n.toLocaleString()
  }

  // 대시보드는 "페이지 권한" 기준으로 표시를 통일한다.
  // - API의 has_permission만 신뢰하면, 페이지 권한이 없는 사용자에게도 0이 노출되는 경우가 있어 UI 일관성이 깨짐.
  const canViewRaw = hasPermission(userInfo, PERMISSIONS.PAGE_RAW_DATA)
  const canViewLabel = hasPermission(userInfo, PERMISSIONS.PAGE_LABEL_DATA)
  const canViewDataset = hasPermission(userInfo, PERMISSIONS.PAGE_DATASET)
  const canViewTestDataset = hasPermission(userInfo, PERMISSIONS.PAGE_TEST_DATASET)
  const canViewTraining = hasPermission(userInfo, PERMISSIONS.PAGE_TRAINING)
  const canViewDeployment = hasPermission(userInfo, PERMISSIONS.PAGE_DEPLOYMENT)

  const noPermissionCard = { count: '—', badgeText: '권한 없음', badgeColor: 'bg-slate-100 text-slate-500' }

  const rawCard = summary?.raw?.has_permission
    ? {
        count: formatCount(summary.raw.total_files),
        badgeText: `+${formatCount(summary.raw.recent_7d_uploads)} (7일)`,
        badgeColor: 'bg-blue-100 text-blue-700',
      }
    : noPermissionCard

  const labelCard = summary?.label?.has_permission
    ? {
        count: formatCount(summary.label.matched_labels),
        badgeText: `+${formatCount(summary.label.recent_7d_uploads)} (7일)`,
        badgeColor: 'bg-indigo-100 text-indigo-700',
      }
    : noPermissionCard

  const datasetCard = summary?.dataset?.has_permission
    ? {
        count: formatCount(summary.dataset.dataset_count),
        badgeText: `+${formatCount(summary.dataset.recent_7d_datasets)} (7일)`,
        badgeColor: 'bg-slate-100 text-slate-700',
      }
    : noPermissionCard

  const testDatasetCard = summary?.test_dataset?.has_permission
    ? {
        count: formatCount(summary.test_dataset.ready_count),
        badgeText: `+${formatCount(summary.test_dataset.recent_7d_datasets)} (7일)`,
        badgeColor: 'bg-purple-100 text-purple-700',
      }
    : noPermissionCard

  const trainingCard = summary?.training?.has_permission
    ? {
        count: formatCount(summary.training.total_runs),
        badgeText: `+${formatCount(summary.training.recent_7d_runs)} (7일)`,
        badgeColor: 'bg-orange-100 text-orange-700',
      }
    : noPermissionCard

  const deployCount = summary?.deployment?.has_permission
    ? summary.deployment.total_deployments
    : null
  const recent7dDeploy = summary?.deployment?.recent_7d_deployments ?? 0
  const deployCard = summary?.deployment?.has_permission
    ? {
        count: formatCount(deployCount),
        badgeText: recent7dDeploy > 0 ? `최근 7일 ${recent7dDeploy}건` : '최근 7일 없음',
        badgeColor: recent7dDeploy > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500',
      }
    : noPermissionCard

  // 업로드 상태 분류 (키워드 추측 폐기 → 실제 status 값 집합 기반)
  // 라이프사이클: UPLOADING → UPLOADED(전처리 대기) → PREPROCESSING(비식별화) → COMPLETED/FAILED
  type StatusKind = 'running' | 'failed' | null
  const RUNNING_STATUSES = new Set([
    'UPLOADING',
    'UPLOADED',
    'PREPROCESSING',
    'PROCESSING',
    'RUNNING',
    'QUEUED',
    'IN_PROGRESS',
  ])
  const FAILED_STATUSES = new Set(['FAILED', 'ERROR', 'CANCELLED', 'CANCELED'])
  const classifyStatus = (s?: string | null): StatusKind => {
    const v = (s || '').toUpperCase()
    if (FAILED_STATUSES.has(v)) return 'failed'
    if (RUNNING_STATUSES.has(v)) return 'running'
    return null
  }
  const uploadStatusLabel = (s?: string | null): string => {
    const v = (s || '').toUpperCase()
    const map: Record<string, string> = {
      UPLOADING: '업로드 중',
      UPLOADED: '전처리 대기',
      PREPROCESSING: '비식별화 처리 중',
      PROCESSING: '처리 중',
      COMPLETED: '완료',
      FAILED: '실패',
    }
    return map[v] || s || ''
  }
  // 경과 표기 — 분 → 시간 → 일, 7일이 넘으면 날짜(이 브라우저 시각 기준)로.
  // 시간만 쓰면 3주 전이 "510시간 51분 전"이 된다.
  const formatMinutesAgo = (m?: number | null): string => {
    if (m == null) return '기록 없음'
    if (m <= 0) return '방금 전'
    if (m < 60) return `${m}분 전`
    if (m < 60 * 24) return `${Math.floor(m / 60)}시간 전`
    if (m < 60 * 24 * 7) return `${Math.floor(m / (60 * 24))}일 전`
    const d = new Date(Date.now() - m * 60_000)
    const sameYear = d.getFullYear() === new Date().getFullYear()
    const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    return `${sameYear ? '' : `${d.getFullYear()}년 `}${d.getMonth() + 1}월 ${d.getDate()}일 ${hhmm}`
  }
  // 목록이 비었을 때 — 기간 안 작업이 없다는 것과 마지막 시각을 함께 (멈춘 건지 판단할 수 있게)
  const emptyWithLast = (what: string, m?: number | null): string =>
    m == null ? `${what} 이력이 없습니다.` : `최근 7일 ${what} 없음 · 마지막 ${what} ${formatMinutesAgo(m)}`
  // 상태 칩(색상+라벨) — 진행 중/완료/실패 구분
  const statusChip = (s?: string | null): { label: string; cls: string } => {
    const kind = classifyStatus(s)
    if (kind === 'failed') return { label: uploadStatusLabel(s) || '실패', cls: 'border-red-100 bg-red-50 text-red-600' }
    if (kind === 'running') return { label: uploadStatusLabel(s) || '진행 중', cls: 'border-blue-100 bg-blue-50 text-blue-600' }
    return { label: uploadStatusLabel(s) || '완료', cls: 'border-slate-200 bg-slate-100 text-slate-500' }
  }

  const autoIngest = activities?.auto_ingest ?? null
  const canViewAuto = activities?.can_view_auto_ingest ?? false

  // 내 업로드 최근 이력 (진행 중/완료 모두 — 끝나도 남김)
  const manualRecent = useMemo(
    () => (activities?.recent_uploads ?? []).slice(0, 5),
    [activities],
  )

  // Alerts = 자동수집 검증 실패(권한자) + 수동 업로드 실패
  const alertItems = useMemo(() => {
    const items: { title: string; detail?: string }[] = []
    if (canViewAuto && autoIngest?.recent_failures?.length) {
      autoIngest.recent_failures.slice(0, 3).forEach((f) => {
        items.push({
          title: `자동수집 검증 실패: ${f.reason_label || f.reason}`,
          detail: f.trigger_id || f.message || undefined,
        })
      })
    }
    ;(activities?.recent_uploads ?? [])
      .filter((u) => classifyStatus(u.status) === 'failed')
      .forEach((u) => {
        items.push({ title: `업로드 실패: ${u.source_filename}`, detail: uploadStatusLabel(u.status) })
      })
    return items.slice(0, 4)
  }, [activities, autoIngest, canViewAuto])

  return (
    <div className="bg-white font-sans text-slate-800">
      {/* 헤더 영역 (버튼 삭제 및 한글화) */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">MLOps 관제 센터</h1>
        <p className="mt-1 text-sm text-slate-500">실시간 플랫폼 모니터링 및 통합 관리</p>
      </div>

      {/* Zone 1: 시스템 상태 (배경색 변경됨) */}
      <SystemStatusStrip status={systemStatus} />

      {/* Zone 2: 파이프라인 KPI (한글화) */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6">
        <PipelineCard
          title="원천 데이터"
          count={rawCard.count}
          sub="총 파일 수"
          icon={Database}
          color="text-blue-600"
          badgeText={rawCard.badgeText}
          badgeColor={rawCard.badgeColor}
        />
        <PipelineCard
          title="라벨링 데이터"
          count={labelCard.count}
          sub="작업 완료"
          icon={Tag}
          color="text-indigo-600"
          badgeText={labelCard.badgeText}
          badgeColor={labelCard.badgeColor}
        />
        <PipelineCard
          title="학습 데이터셋"
          count={datasetCard.count}
          sub="생성된 데이터셋"
          icon={Book}
          color="text-slate-700"
          badgeText={datasetCard.badgeText}
          badgeColor={datasetCard.badgeColor}
        />
        <PipelineCard
          title="평가 데이터셋"
          count={testDatasetCard.count}
          sub="준비된 데이터셋"
          icon={FlaskConical}
          color="text-purple-600"
          badgeText={testDatasetCard.badgeText}
          badgeColor={testDatasetCard.badgeColor}
        />
        <PipelineCard
          title="학습 이력"
          count={trainingCard.count}
          sub="등록된 학습 기록"
          icon={Layers}
          color="text-orange-600"
          badgeText={trainingCard.badgeText}
          badgeColor={trainingCard.badgeColor}
        />
        <PipelineCard
          title="모델 배포"
          count={deployCard.count}
          sub="서빙 중인 모델"
          icon={Box}
          color="text-green-600"
          badgeText={deployCard.badgeText}
          badgeColor={deployCard.badgeColor}
        />
      </div>

      {/* Zone 3: 차트 및 로그 (2:1 비율) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* 왼쪽: 분석 차트 영역 */}
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-6">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h3 className="flex items-center gap-2 text-lg font-bold text-slate-800">
                <Activity size={20} className="text-blue-500" />
                월별 데이터 수집 현황
              </h3>
              {/* 수치 강조: 현재 차트에 바인딩된 데이터 기준으로 자동/수동 모두 표기 */}
              <p className="mt-1 text-sm text-slate-500">
                총 <span className="font-bold text-slate-800">{chartTotal.toLocaleString()}</span>
                건 (자동:{' '}
                <span className="font-bold text-blue-600">{chartAuto.toLocaleString()}</span> / 수동:{' '}
                <span className="font-bold text-slate-800">{chartManual.toLocaleString()}</span>)
              </p>
            </div>

            {/* 필터 */}
            <select
              className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-600 outline-none"
              value={selectedYear ?? ''}
              onChange={(e) => {
                const y = parseInt(e.target.value, 10)
                if (!Number.isNaN(y)) setSelectedYear(y)
              }}
            >
              {(summary?.raw.available_years && summary.raw.available_years.length > 0
                ? summary.raw.available_years
                : [new Date().getFullYear()]
              ).map((y) => (
                <option key={y} value={y}>
                  {y}년
                </option>
              ))}
            </select>
          </div>

          {/* 차트 영역 */}
          <MonthlyBarChart data={chartData} />
          {loading && <div className="mt-2 text-xs text-slate-400">차트 데이터를 불러오는 중...</div>}
          {error && !loading && <div className="mt-2 text-xs text-rose-600">{error}</div>}
        </div>

        {/* 오른쪽: 작업 현황 패널 (한글화) */}
        {/* h-full: 왼쪽 차트 높이에 맞춤. 내용이 넘치면 아래 내부 영역에서 스크롤 */}
        <div className="flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white lg:col-span-1">
          <div className="flex items-center justify-between rounded-t-xl border-b border-slate-100 bg-slate-50 p-4">
            <h3 className="text-sm font-bold text-slate-700">실시간 작업 현황</h3>
            {/* 상태 점: 카나리아 실패(시스템 오류) 시 빨간 점멸로 격상 */}
            {autoIngest?.anonymization?.canary_status === 'failed' ? (
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
              </span>
            ) : (
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
              </span>
            )}
          </div>

          {/* relative+absolute: 내용이 패널 높이를 밀어올리지 않게 하여 왼쪽 차트 높이에 고정,
              내용이 넘치면 이 영역에서만 세로 스크롤 */}
          <div className="relative min-h-0 flex-1">
            <div className="absolute inset-0 space-y-4 overflow-y-auto p-4">
            {/* 자동수집 (NAS 비식별화) — '수집 리포트' 권한 보유자에게만 표시 */}
            {canViewAuto && (
              <div>
                <h4 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase text-slate-400">
                  <Database size={13} /> 자동수집 (NAS 비식별화)
                </h4>
                {/* 비식별화 시스템 오류 — 카나리아 실패 시 해소될 때까지 고정 표시.
                    흘러가는 이벤트가 아니라 '상태'다 (2026-07/08 무신호 사고 재발 방지) */}
                {autoIngest?.anonymization?.canary_status === 'failed' && (
                  <div className="mb-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs">
                    <div className="flex items-center gap-2 font-bold text-rose-700">
                      <AlertCircle size={14} />
                      비식별화 시스템 오류 — 처리 중단됨
                    </div>
                    <div className="mt-1 text-rose-600">
                      카나리아 검사 실패
                      {autoIngest.anonymization.canary_checked_at &&
                        ` (${formatLocaleDatetime(autoIngest.anonymization.canary_checked_at)})`}
                      {autoIngest.anonymization.canary_error && ` · ${autoIngest.anonymization.canary_error}`}
                    </div>
                    <div className="mt-1 text-rose-500">
                      신규 이미지는 처리되지 않고 대기 중입니다. 복구 후 자동 재개됩니다.
                    </div>
                  </div>
                )}
                {autoIngest?.anonymization?.canary_status === 'not_configured' && (
                  <div className="mb-2 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs font-semibold text-amber-700">
                    <AlertCircle size={14} /> 비식별화 카나리아 미설정 — 시스템 고장을 감지할 수 없습니다
                  </div>
                )}
                {(autoIngest?.anonymization?.quarantined_count ?? 0) > 0 && (
                  <div className="mb-2 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs">
                    <AlertCircle size={14} className="text-amber-600" />
                    <span className="font-semibold text-amber-700">
                      비식별화 격리 {autoIngest!.anonymization!.quarantined_count.toLocaleString()}건 재처리 대기
                      {(autoIngest!.anonymization!.quarantined_exhausted ?? 0) > 0 &&
                        ` (재시도 소진 ${autoIngest!.anonymization!.quarantined_exhausted!.toLocaleString()}건 — 확인 필요)`}
                      {autoIngest!.anonymization!.quarantined_oldest_at &&
                        ` · 최초 ${formatLocaleDateOnly(autoIngest!.anonymization!.quarantined_oldest_at)}`}
                    </span>
                  </div>
                )}
                {/* 진행 중 배지 (돌고 있을 때만) */}
                {autoIngest?.in_progress && (
                  <div className="mb-2 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 p-2 text-xs">
                    <RefreshCw size={14} className="animate-spin text-blue-600" />
                    <span className="font-semibold text-blue-700">
                      {autoIngest.processing_count > 0
                        ? `비식별화 처리 중 · ${autoIngest.processing_count}건`
                        : '수집 진행 중'}
                    </span>
                  </div>
                )}
                {/* 최근 수집 회차 이력 — 끝나도 남음 */}
                {(autoIngest?.recent_batches?.length ?? 0) === 0 ? (
                  <div className="rounded-lg border border-slate-100 bg-slate-50 p-4 text-center text-sm text-slate-400">
                    {emptyWithLast('수집', autoIngest?.last_ingest_minutes_ago)}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {autoIngest!.recent_batches.map((b) => {
                      const chip = statusChip(b.status)
                      return (
                        <div
                          key={`auto-${b.id}`}
                          className="flex items-start gap-2 rounded-lg border border-slate-100 bg-white p-2 text-xs text-slate-700"
                        >
                          <Clock size={14} className="mt-0.5 text-slate-400" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate font-semibold">
                                {[b.project_name, b.node_name].filter(Boolean).join(' / ') || `배치 #${b.id}`}
                              </span>
                              <span className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${chip.cls}`}>
                                {chip.label}
                              </span>
                            </div>
                            <div className="truncate text-[11px] text-slate-400">
                              {formatMinutesAgo(b.minutes_ago)} · {b.item_count.toLocaleString()}건
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
                <div className="mt-2 text-[11px] text-slate-400">
                  최근 1시간 신규: {(autoIngest?.recent_1h_files ?? 0).toLocaleString()}건
                  {autoIngest && autoIngest.pending_count > 0
                    ? ` · 전처리 대기 ${autoIngest.pending_count.toLocaleString()}건`
                    : ''}
                </div>
              </div>
            )}

            {/* 내 업로드 (수동) — 최근 이력(완료 포함, 끝나도 남음) */}
            <div>
              <h4 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase text-slate-400">
                <Upload size={13} /> 내 업로드
              </h4>
              {manualRecent.length === 0 ? (
                <div className="rounded-lg border border-slate-100 bg-slate-50 p-4 text-center text-sm text-slate-400">
                  {emptyWithLast('업로드', activities?.last_upload_minutes_ago)}
                </div>
              ) : (
                <div className="space-y-2">
                  {manualRecent.map((u) => {
                    const chip = statusChip(u.status)
                    return (
                      <div
                        key={`up-${u.id}`}
                        className="flex items-start gap-2 rounded-lg border border-slate-100 bg-white p-2 text-xs text-slate-700"
                      >
                        <Clock size={14} className="mt-0.5 text-slate-400" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate font-semibold">{u.source_filename}</span>
                            <span className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${chip.cls}`}>
                              {chip.label}
                            </span>
                          </div>
                          <div className="truncate text-[11px] text-slate-400">{formatMinutesAgo(u.minutes_ago)}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* 알림 영역 */}
            <div>
              <h4 className="mb-2 text-xs font-bold uppercase text-slate-400">Alerts</h4>
              {alertItems.length === 0 ? (
                <div className="flex items-start gap-3 rounded-lg border border-green-100 bg-green-50 p-3 text-sm">
                  <CheckCircle size={16} className="mt-0.5 shrink-0 text-green-500" />
                  <span className="text-green-700">최근 수집·업로드 실패가 없습니다.</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {alertItems.map((it, idx) => (
                    <div
                      key={`alert-${idx}`}
                      className="flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 p-2 text-xs text-red-700"
                    >
                      <AlertCircle size={14} className="mt-0.5 text-red-600" />
                      <div className="min-w-0">
                        <div className="truncate font-semibold" title={it.title}>{it.title}</div>
                        {it.detail && (
                          <div
                            className="break-all text-[11px] text-red-600/80"
                            title={it.detail}
                          >
                            {it.detail}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
          </div>

          {/* '전체 로그 보기'는 현재 동작하지 않아 사용자 혼란을 방지하기 위해 제거 */}
        </div>
      </div>
    </div>
  )
}

export default Dashboard


